import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FileText,
  Download,
  Search,
  RefreshCw,
  Loader2,
  Eye,
  ChevronLeft,
  ChevronRight,
  FileCheck,
  DollarSign,
  Calendar,
  Sparkles,
  Mail,
  Wrench,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import InvoicePreview from '@/components/invoice/InvoicePreview';

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    totalAmount: 0
  });
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Download state
  const [downloadingId, setDownloadingId] = useState(null);
  const hiddenPreviewRef = useRef(null);
  
  // Sending email state
  const [sendingEmail, setSendingEmail] = useState({});

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50'
      });
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await adminAPI.getInvoices(Object.fromEntries(params));
      setInvoices(response.data.invoices || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);

      // Use global stats from API
      const apiStats = response.data.stats || {};
      const pageAmount = response.data.invoices?.reduce((sum, i) => sum + (i.total || 0), 0) || 0;
      setStats({
        total: response.data.total || 0,
        paid: apiStats.total_paid || 0,
        pending: apiStats.total_pending || 0,
        totalAmount: apiStats.total_revenue || pageAmount
      });
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchInvoices();
  };

  const handleGenerateBulk = async () => {
    setGenerating(true);
    try {
      const response = await adminAPI.generateBulkInvoices({ status_filter: 'completed', limit: 500 });
      toast.success(`Generated ${response.data.count} invoices`);
      fetchInvoices();
    } catch (error) {
      console.error('Error generating invoices:', error);
      toast.error('Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  };

  const handleFixZeroTotals = async () => {
    setFixing(true);
    try {
      const response = await adminAPI.fixZeroTotalInvoices();
      toast.success(`Fixed ${response.data.count} invoices with zero totals`);
      fetchInvoices();
    } catch (error) {
      console.error('Error fixing invoices:', error);
      toast.error('Failed to fix invoices');
    } finally {
      setFixing(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      // First fetch the order details
      const response = await adminAPI.getInvoiceOrderDetails(invoice.order_id);
      const orderData = transformOrderForPreview(response.data);
      
      // Create a temporary container for the invoice preview
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '800px';
      document.body.appendChild(container);
      
      // Import ReactDOM and render the preview
      const ReactDOM = await import('react-dom/client');
      const root = ReactDOM.createRoot(container);
      
      // Create a wrapper that gives us access to the rendered content
      const InvoiceWrapper = () => (
        <div id="pdf-render-target">
          <InvoicePreview order={orderData} hideActions={true} />
        </div>
      );
      
      root.render(<InvoiceWrapper />);
      
      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get the invoice content div
      const invoiceContent = container.querySelector('#invoice-content');
      
      if (!invoiceContent) {
        throw new Error('Invoice content not found');
      }
      
      // Use html2pdf to generate PDF
      const html2pdf = (await import('html2pdf.js')).default;
      
      const invoiceNumber = `INV-${invoice.order_number?.replace('GRC-WEB-', '') || invoice.invoice_number}`;
      
      const opt = {
        margin: 0,
        filename: `${invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(invoiceContent).save();
      
      // Cleanup
      root.unmount();
      document.body.removeChild(container);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = async (invoice) => {
    setLoadingPreview(true);
    setPreviewOpen(true);
    try {
      const response = await adminAPI.getInvoiceOrderDetails(invoice.order_id);
      setPreviewOrder(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load invoice preview');
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendEmail = async (invoice) => {
    if (!invoice.customer_email) {
      toast.error('No customer email available for this invoice');
      return;
    }
    
    setSendingEmail(prev => ({ ...prev, [invoice.id]: true }));
    try {
      const response = await adminAPI.sendInvoiceEmail(invoice.order_id);
      toast.success(response.data.message || `Invoice email sent to ${invoice.customer_email}`);
    } catch (error) {
      console.error('Error sending invoice email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice email');
    } finally {
      setSendingEmail(prev => ({ ...prev, [invoice.id]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Transform order data for InvoicePreview component
  const transformOrderForPreview = (order) => {
    if (!order) return null;
    
    const total = order.calculated_total || order.total || order.subtotal || 0;
    
    return {
      order_number: order.order_number || order.legacy_order_number,
      created_at: order.created_at,
      status: order.status,
      payment_status: order.status === 'completed' ? 'paid' : 'pending',
      payment_method: order.payment_method || 'Credit Card',
      customer_name: order.customer_name || `${order.billing_first_name || ''} ${order.billing_last_name || ''}`.trim(),
      billing_email: order.billing_email || order.customer_email,
      billing_phone: order.billing_phone,
      billing_company: order.billing_company,
      billing_address: order.billing_address,
      billing_city: order.billing_city,
      billing_state: order.billing_state,
      billing_zip: order.billing_zip,
      billing_country: order.billing_country,
      items: (order.items || []).map(item => ({
        webinar_code: item.webinar_code || item.model,
        webinar_title: item.webinar_title || item.title || item.name,
        variant: item.variant || item.variant_normalized || 'Webinar Access',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || item.price || 0,
        total_price: item.total_price || item.total || item.unit_price || item.price || 0,
        scheduled_date: item.scheduled_date,
        speaker: item.speaker
      })),
      subtotal: order.subtotal || total,
      discount_amount: order.discount_amount || 0,
      tax_amount: order.tax_amount || 0,
      total_amount: total
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Invoice Management</h1>
          <p className="text-slate-500 mt-1">Manage and download invoices for all orders</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleFixZeroTotals}
            disabled={fixing}
            variant="outline"
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            data-testid="fix-zero-totals-btn"
          >
            {fixing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wrench className="w-4 h-4 mr-2" />
            )}
            Fix $0 Invoices
          </Button>
          <Button
            onClick={handleGenerateBulk}
            disabled={generating}
            className="bg-slate-800 hover:bg-slate-900"
            data-testid="generate-invoices-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generate Missing Invoices
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Invoices</p>
                <p className="text-xl font-bold text-slate-800">{total.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Paid</p>
                <p className="text-xl font-bold text-green-600">{stats.paid}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-xl font-bold text-amber-600">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-xl font-bold text-purple-600">{formatCurrency(stats.totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4 items-center flex-wrap">
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by invoice #, order #, customer name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="invoice-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button type="button" variant="outline" onClick={fetchInvoices}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Invoices</span>
            <span className="text-sm font-normal text-slate-500">
              Showing {invoices.length} of {total.toLocaleString()} invoices
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No invoices found</p>
              <p className="text-sm text-slate-400 mt-1">
                Click "Generate Missing Invoices" to create invoices for completed orders
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Order #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} className="hover:bg-slate-50">
                      <TableCell>
                        <span className="font-mono text-sm font-medium text-slate-700">
                          {invoice.invoice_number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-slate-500">
                          {invoice.order_number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-800">{invoice.customer_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{invoice.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {formatDate(invoice.invoice_date)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-semibold ${invoice.total === 0 ? 'text-red-500' : 'text-slate-800'}`}>
                          {formatCurrency(invoice.total)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            invoice.status === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }
                        >
                          {invoice.status === 'paid' ? 'Paid' : 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(invoice)}
                            title="Preview Invoice"
                            data-testid={`preview-invoice-${invoice.invoice_number}`}
                            className="text-slate-600 hover:text-slate-800"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPdf(invoice)}
                            disabled={downloadingId === invoice.id}
                            title="Download PDF"
                            data-testid={`download-invoice-${invoice.invoice_number}`}
                            className="text-slate-600 hover:text-slate-800"
                          >
                            {downloadingId === invoice.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendEmail(invoice)}
                            disabled={sendingEmail[invoice.id] || !invoice.customer_email}
                            title={invoice.customer_email ? `Send to ${invoice.customer_email}` : 'No email available'}
                            data-testid={`send-invoice-${invoice.invoice_number}`}
                            className="text-slate-600 hover:text-blue-600"
                          >
                            {sendingEmail[invoice.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <DialogTitle>Invoice Preview</DialogTitle>
              <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
          {loadingPreview ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
            </div>
          ) : previewOrder ? (
            <div className="p-0">
              <InvoicePreview order={transformOrderForPreview(previewOrder)} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
